import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import { InlineAffiliateCTA } from '@/components/affiliate/InlineAffiliateCTA';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';

const AffiliateCtaPreview = ({ node }: any) => {
  const { partnerId, displayType, context } = node.attrs;

  const { data: partner, isLoading } = useQuery({
    queryKey: ['affiliate-partner-preview', partnerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('affiliate_partners')
        .select('*')
        .eq('id', partnerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!partnerId,
  });

  if (isLoading) {
    return (
      <NodeViewWrapper className="my-4">
        <Card className="p-4 border-dashed border-2 border-primary/30">
          <p className="text-sm text-muted-foreground">Loading affiliate CTA preview...</p>
        </Card>
      </NodeViewWrapper>
    );
  }

  if (!partner) {
    return (
      <NodeViewWrapper className="my-4">
        <Card className="p-4 border-dashed border-2 border-destructive/30">
          <p className="text-sm text-destructive">Affiliate partner not found</p>
        </Card>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="my-4">
      <div className="relative">
        <div className="absolute -top-2 -left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded z-10">
          Affiliate CTA Preview
        </div>
        <InlineAffiliateCTA
          partner={partner}
          context={context}
          compact={displayType === 'compact'}
        />
      </div>
    </NodeViewWrapper>
  );
};

export const AffiliateCtaExtension = Node.create({
  name: 'affiliateCta',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      partnerId: {
        default: null,
      },
      displayType: {
        default: 'full',
      },
      context: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-affiliate-cta]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-affiliate-cta': '' }),
      `<!-- AFFILIATE_CTA:${HTMLAttributes.partnerId}:${HTMLAttributes.displayType}:${HTMLAttributes.context || ''} -->`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AffiliateCtaPreview);
  },
});
